'use client';

import React from 'react';
import { Container } from 'components/shared';
import { H2, P } from '../StyleConstants';
import BiomarkerTable from './BiomarkerTable';
import { PLANS } from './constants';
import styles from './index.module.css';

const cx = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(' ');

type ComparePlansProps = {
  isModal?: boolean;
  className?: string;
};

const ComparePlans = ({ className, isModal }: ComparePlansProps) => {
  const plans = PLANS;

  type Row = React.ReactNode[];
  const rows = React.useMemo(() => {
    const built: Row[] = [];
    // Title row
    built.push(
      plans.map((p) => (
        <h3 className={styles.planTitle} key={`${p.key}-title`}>
          {p.title}
        </h3>
      )),
    );
    // Subtitle row
    built.push(
      plans.map((p) => (
        <P className={styles.subtitle} key={`${p.key}-subtitle`}>
          {p.subtitle}
        </P>
      )),
    );
    // Price row
    built.push(
      plans.map((p) => (
        <div className={styles.priceRow} key={`${p.key}-price`}>
          <span className={styles.price}>{p.price}</span>
          <span className={styles.perYear}>{p.perYear}</span>
        </div>
      )),
    );

    const maxFeatureRows = plans.reduce(
      (m, p) => Math.max(m, p.list?.length || 0),
      0,
    );
    for (let i = 0; i < maxFeatureRows; i += 1) {
      built.push(
        plans.map((p) => {
          const item = p.list?.[i];
          return item ? (
            <div
              key={`${p.key}-feature-${i}`}
              className={cx(
                styles.headerListItem,
                !item.available && styles.disabled,
              )}
            >
              <span className={styles.iconWrapper}>
                {item.available ? CheckIcon : CrossIcon}
              </span>
              <span>{item.label}</span>
            </div>
          ) : null;
        }),
      );
    }
    return built;
  }, [plans]);

  return (
    <section
      className={cx(styles.root, className)}
      data-modal={isModal ? 'true' : undefined}
    >
      <Container style={{ position: 'relative' }}>
        <H2 className={styles.heading}>Blood Vision Plans</H2>
        <div className={styles.tableWrapper}>
          <table>
            <tbody>
              {rows.map((cells, idx) => (
                <tr key={idx}>
                  {cells.map((cell, cIdx) => (
                    <td key={cIdx} style={{ width: `${100 / plans.length}%` }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <BiomarkerTable isModal={!!isModal} />
      </Container>
    </section>
  );
};

const CheckIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="33"
    height="32"
    viewBox="0 0 33 32"
    fill="none"
  >
    <path
      d="M21.5 12.25L14.625 19.75L11.5 16.3409"
      stroke="black"
      strokeWidth="1.36364"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CrossIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="33"
    height="32"
    viewBox="0 0 33 32"
    fill="none"
  >
    <path
      d="M20.5 12L12.5 20"
      stroke="black"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.5 12L20.5 20"
      stroke="black"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default ComparePlans;
